import type { PesticideCatalogProduct, PesticideCatalogRate } from "@/type/api";

export interface LifeStageTarget {
	code: string;
	title: string;
}

/** The life-stage icon set: the four larval instars, pupae and adults. */
export const LIFE_STAGE_TARGETS: LifeStageTarget[] = [
	{ code: "I1", title: "Instar Stage 1" },
	{ code: "I2", title: "Instar Stage 2" },
	{ code: "I3", title: "Instar Stage 3" },
	{ code: "I4", title: "Instar Stage 4" },
	{ code: "P", title: "Pupae" },
	{ code: "A", title: "Adults" },
];

const INSTAR_RANGE =
	/(\d)(?:st|nd|rd|th)\s*(?:-|–|—|through|to)\s*(\d)(?:st|nd|rd|th)/g;
const INSTAR_SINGLE = /(\d)(?:st|nd|rd|th)/g;
const LARVA = /\blarvae?\b|\blarval\b/g;

/**
 * Maps the package's free-text target life stages onto the life-stage icon set.
 * Only stages the text states are activated: a bare "larvae" means every
 * instar, an explicit band ("2nd-4th instar larvae") means exactly that band,
 * and an adult statement ("Adult", "adults", "adult emergence") activates the
 * adult circle. Anything the icon set still cannot express or that is ambiguous
 * (for example "Immature mosquito stages") activates nothing rather than
 * guessing. The raw text is never rendered — the circles are the field.
 */
export function activeLifeStages(text: string | null | undefined): Set<string> {
	const active = new Set<string>();
	if (!text) {
		return active;
	}
	// A curator's own selection is stored as the canonical codes ("I1; I4"),
	// while the package's catalog text is prose ("2nd-4th instar larvae"). The
	// codes are exact, so read them first: a value made of codes is never
	// prose-parseable, and vice versa, so the two readings cannot disagree.
	const stored = parseLifeStageCodes(text);
	if (stored.size > 0) {
		return stored;
	}
	const lowered = text.toLowerCase();

	for (const match of lowered.matchAll(INSTAR_RANGE)) {
		for (let instar = Number(match[1]); instar <= Number(match[2]); instar++) {
			if (instar >= 1 && instar <= 4) {
				active.add(`I${instar}`);
			}
		}
	}

	for (const match of lowered.matchAll(INSTAR_SINGLE)) {
		const instar = Number(match[1]);
		if (instar >= 1 && instar <= 4) {
			active.add(`I${instar}`);
		}
	}

	for (const match of lowered.matchAll(LARVA)) {
		// "2nd-4th instar larvae" bands the larval stage; only an unqualified
		// mention of larvae covers all four instars.
		if (/\binstar\s+$/.test(lowered.slice(0, match.index ?? 0))) {
			continue;
		}
		active.add("I1");
		active.add("I2");
		active.add("I3");
		active.add("I4");
	}

	if (/\bpupae?\b/.test(lowered)) {
		active.add("P");
	}

	if (/\badults?\b/.test(lowered)) {
		active.add("A");
	}

	return active;
}

/** The canonical life-stage codes, in the icon set's display order. */
export const LIFE_STAGE_CODES: string[] = LIFE_STAGE_TARGETS.map(
	(target) => target.code,
);

/**
 * The district's own stored target selection ("I1; I2"; null/empty = none) as a
 * set of icon-set codes. It is the district's curation, stored as codes rather
 * than the catalog's free text, so unlike activeLifeStages it needs no parsing
 * of prose — an unknown token is ignored rather than guessed.
 */
export function parseLifeStageCodes(
	value: string | null | undefined,
): Set<string> {
	const codes = new Set<string>();
	if (!value) {
		return codes;
	}
	for (const part of value.split(";")) {
		const code = part.trim();
		if (LIFE_STAGE_CODES.includes(code)) {
			codes.add(code);
		}
	}
	return codes;
}

/**
 * The canonical stored string for a set of codes: the icon set's order, joined
 * with "; " ("I1; I4; P"); empty when none are selected, which the API clears to
 * NULL.
 */
export function serializeLifeStageCodes(codes: Iterable<string>): string {
	const selected = new Set(codes);
	return LIFE_STAGE_CODES.filter((code) => selected.has(code)).join("; ");
}

interface RateGroup {
	low: number;
	high: number;
	unit: string;
	basis: string;
}

/** The catalog's rate span across every application scenario, grouped by unit. */
export function formatRateRange(rates: PesticideCatalogRate[]): string {
	const groups = new Map<string, RateGroup>();
	for (const rate of rates) {
		const low = rate.rate_low ?? rate.rate_high;
		const high = rate.rate_high ?? rate.rate_low;
		if (low === null || high === null) {
			continue;
		}
		const unit = rate.rate_unit ?? "";
		const basis = rate.rate_basis ?? "";
		const group = groups.get(`${unit}|${basis}`);
		if (group) {
			group.low = Math.min(group.low, low);
			group.high = Math.max(group.high, high);
			continue;
		}
		groups.set(`${unit}|${basis}`, { low, high, unit, basis });
	}
	return Array.from(groups.values(), (group) => {
		const bounds =
			group.low === group.high ? `${group.low}` : `${group.low}–${group.high}`;
		return [bounds, group.unit, group.basis]
			.filter((part) => part !== "")
			.join(" ");
	}).join("; ");
}

/**
 * One rate's projection into the district's preferred units, when the API
 * supplied one that actually differs from the label. The label's own statement
 * stays the source of truth; this is the side-by-side comparison.
 */
export function formatPreferredRate(rate: PesticideCatalogRate): string {
	const preferred = rate.preferred;
	if (!preferred || !preferred.converted) {
		return "";
	}
	const low = preferred.rate_low ?? preferred.rate_high;
	const high = preferred.rate_high ?? preferred.rate_low;
	const bounds =
		low === null || high === null
			? ""
			: low === high
				? `${low}`
				: `${low}–${high}`;
	return [bounds, preferred.rate_unit ?? "", preferred.rate_basis ?? ""]
		.filter((part) => part !== "")
		.join(" ");
}

/** The residual days span a product's scenarios cover, as numbers. */
export function residualSpan(
	rates: PesticideCatalogRate[],
): { low: number; high: number } | null {
	const lows = rates
		.map((rate) => rate.residual_days_low)
		.filter((days): days is number => days !== null);
	const highs = rates
		.map((rate) => rate.residual_days_high)
		.filter((days): days is number => days !== null);
	if (lows.length === 0 && highs.length === 0) {
		return null;
	}
	const low = lows.length > 0 ? Math.min(...lows) : Math.min(...highs);
	const high = highs.length > 0 ? Math.max(...highs) : Math.max(...lows);
	return { low, high };
}

/** The catalog's residual span in days across every application scenario. */
export function formatResidualDays(rates: PesticideCatalogRate[]): string {
	const span = residualSpan(rates);
	if (span === null) {
		return "";
	}
	return span.low === span.high ? `${span.low}` : `${span.low}–${span.high}`;
}

export type CatalogDocumentFilter = "any" | "label" | "sds" | "both" | "none";

export type CatalogSortKey =
	| "name"
	| "formulation"
	| "category"
	| "activeIngredients"
	| "targets"
	| "residual"
	| "documents";

/**
 * The list page's query state: one search over product name and active
 * ingredients, plus one filter per column. Numbers stay strings because they
 * are bound to text inputs — an empty box means "no bound", which is not the
 * same as zero, and parsing here keeps that distinction in one place.
 */
export interface CatalogFilters {
	search: string;
	name: string;
	formulation: string;
	category: string;
	activeIngredients: string;
	target: string;
	residualMin: string;
	residualMax: string;
	rateRange: string;
	documents: CatalogDocumentFilter;
}

/** A fresh, unfiltered query. */
export function emptyCatalogFilters(): CatalogFilters {
	return {
		search: "",
		name: "",
		formulation: "",
		category: "",
		activeIngredients: "",
		target: "",
		residualMin: "",
		residualMax: "",
		rateRange: "",
		documents: "any",
	};
}

/** Whether any control is narrowing the list, for enabling "Clear filters". */
export function hasActiveCatalogFilters(filters: CatalogFilters): boolean {
	return (
		filters.search.trim() !== "" ||
		filters.name.trim() !== "" ||
		filters.formulation !== "" ||
		filters.category !== "" ||
		filters.activeIngredients.trim() !== "" ||
		filters.target !== "" ||
		filters.residualMin.trim() !== "" ||
		filters.residualMax.trim() !== "" ||
		filters.rateRange.trim() !== "" ||
		filters.documents !== "any"
	);
}

const documentCount = (product: PesticideCatalogProduct): number =>
	(product.label_document_url ? 1 : 0) + (product.sds_document_url ? 1 : 0);

const lowestLifeStage = (product: PesticideCatalogProduct): number =>
	LIFE_STAGE_TARGETS.findIndex((target) =>
		activeLifeStages(product.target_life_stages).has(target.code),
	);

/**
 * Whether a product survives the page's query. Text filters are case-insensitive
 * substring matches on the trimmed value; the residual bounds match any span
 * that overlaps the requested one, so a product covering 7-28 days answers both
 * "min 20" and "max 10".
 */
export function matchesCatalogFilters(
	product: PesticideCatalogProduct,
	filters: CatalogFilters,
): boolean {
	const contains = (haystack: string | null, needle: string) => {
		const query = needle.trim().toLowerCase();
		return query === "" || (haystack ?? "").toLowerCase().includes(query);
	};

	const search = filters.search.trim().toLowerCase();
	if (
		search !== "" &&
		!(
			product.name.toLowerCase().includes(search) ||
			(product.active_ingredients ?? "").toLowerCase().includes(search)
		)
	) {
		return false;
	}

	if (!contains(product.name, filters.name)) return false;
	if (!contains(product.active_ingredients, filters.activeIngredients))
		return false;
	if (!contains(formatRateRange(product.rates), filters.rateRange))
		return false;
	if (
		filters.formulation !== "" &&
		product.formulation !== filters.formulation
	) {
		return false;
	}
	if (filters.category !== "" && product.category !== filters.category) {
		return false;
	}
	if (
		filters.target !== "" &&
		!activeLifeStages(product.target_life_stages).has(filters.target)
	) {
		return false;
	}

	if (filters.residualMin.trim() !== "" || filters.residualMax.trim() !== "") {
		const span = residualSpan(product.rates);
		if (span === null) return false;
		if (
			filters.residualMin.trim() !== "" &&
			span.high < Number(filters.residualMin)
		) {
			return false;
		}
		if (
			filters.residualMax.trim() !== "" &&
			span.low > Number(filters.residualMax)
		) {
			return false;
		}
	}

	const hasLabel = product.label_document_url !== null;
	const hasSds = product.sds_document_url !== null;
	switch (filters.documents) {
		case "label":
			if (!hasLabel) return false;
			break;
		case "sds":
			if (!hasSds) return false;
			break;
		case "both":
			if (!hasLabel || !hasSds) return false;
			break;
		case "none":
			if (hasLabel || hasSds) return false;
			break;
	}

	return true;
}

/**
 * Orders the list by one column. Product, formulation, category and active
 * ingredients sort alphabetically; targets by the earliest stage present in icon
 * order (I1 < I2 < I3 < I4 < P); residual by the span's lower bound, then its
 * upper; documents by how many of the two exist.
 *
 * A product with nothing in the sorted column — no formulation, no active
 * ingredient, no life stages, no residual — sorts last in *both* directions, so
 * reversing the sort cannot answer "which rows are missing this?" as a side
 * effect, and an ascending sort does not open on a screen of blanks. Ties keep
 * the order they arrived in, which is by name. Rate Range is deliberately not
 * sortable: its units differ per product (lb, fl oz, gal, pouch, briquet,
 * tablet), so ordering those numbers against each other produces a meaningless
 * order.
 */
export function sortCatalogProducts(
	products: PesticideCatalogProduct[],
	key: CatalogSortKey,
	descending: boolean,
): PesticideCatalogProduct[] {
	const isMissing = (product: PesticideCatalogProduct): boolean => {
		switch (key) {
			case "formulation":
				return product.formulation === null;
			case "activeIngredients":
				return product.active_ingredients === null;
			case "targets":
				return lowestLifeStage(product) === -1;
			case "residual":
				return residualSpan(product.rates) === null;
			default:
				return false;
		}
	};

	const compare = (a: PesticideCatalogProduct, b: PesticideCatalogProduct) => {
		switch (key) {
			case "name":
				return a.name.localeCompare(b.name);
			case "formulation":
				return (a.formulation ?? "").localeCompare(b.formulation ?? "");
			case "category":
				return a.category.localeCompare(b.category);
			case "activeIngredients":
				return (a.active_ingredients ?? "").localeCompare(
					b.active_ingredients ?? "",
				);
			case "targets":
				return lowestLifeStage(a) - lowestLifeStage(b);
			case "residual": {
				const left = residualSpan(a.rates);
				const right = residualSpan(b.rates);
				if (left === null || right === null) return 0;
				return left.low - right.low || left.high - right.high;
			}
			case "documents":
				return documentCount(a) - documentCount(b);
		}
	};

	return [...products].sort((a, b) => {
		const aMissing = isMissing(a);
		const bMissing = isMissing(b);
		if (aMissing || bMissing) {
			return aMissing === bMissing ? 0 : aMissing ? 1 : -1;
		}
		const order = compare(a, b);
		return descending ? -order : order;
	});
}
